// Nuxt 4 client plugin: @azure/msal-browser authentication shim
// Provides window.msal and injects $msal with methods used by existing code:
// - isAuthenticated()
// - data.user.idToken.{exp,aud}
// - silentLogin(opts)
// - loginRedirect(opts)
// - logout()
import { PublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationResult, INetworkModule, NetworkRequestOptions, NetworkResponse, SilentRequest } from '@azure/msal-browser';
import { defineNuxtPlugin, useCookie, useRuntimeConfig } from 'nuxt/app';
import type { NuxtApp } from 'nuxt/app';
import { consumeRedirectLocation, saveRedirectLocation, hasRedirectLocation } from '~/utils/redirectLocation';
import { TOKEN_COOKIE_OPTIONS } from '~/utils/tokenCookie';
import type { MsalShim } from '~/types/msal';

function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function setTokenCookie(idToken: string) {
  const tokenCookie = useCookie('token', TOKEN_COOKIE_OPTIONS);
  tokenCookie.value = idToken;
}

// Troubleshooting aid: log every AAD /token response (auth-code redemption after the
// redirect, refresh-token renewal inside acquireTokenSilent). MSAL's default FetchClient
// isn't exported, so wrap the live instance in place instead of replacing it — its
// NetworkError wrapping, which MSAL's retry/throttling logic relies on, stays intact.
// MSAL appends telemetry query params to the endpoint, hence the `?` in the match.
// Entries are logged as pre-serialized JSON, not live objects: the redirect redemption
// is followed by window.location.replace, and DevTools can't expand an object whose
// page is gone ("No properties"). Error fields (message, MSAL's nested NetworkError
// .error) aren't enumerable/serializable by default, so the replacer spells them out.
function toLogString(value: unknown) {
  return JSON.stringify(value, (_key, v) => (v instanceof Error ? { ...v, name: v.name, message: v.message } : v), 2);
}

function logTokenResponses(networkClient: INetworkModule) {
  const sendPost = networkClient.sendPostRequestAsync.bind(networkClient) as INetworkModule['sendPostRequestAsync'];
  networkClient.sendPostRequestAsync = async <T>(url: string, options?: NetworkRequestOptions): Promise<NetworkResponse<T>> => {
    if (!/\/token(\?|$)/.test(url)) return sendPost<T>(url, options);
    try {
      const response = await sendPost<T>(url, options);
      const log = response.status >= 400 ? console.warn : console.log;
      log(`[msal] /token response ${response.status}\n${toLogString({ url, headers: response.headers, body: response.body })}`);
      return response;
    } catch (error) {
      console.error(`[msal] /token request failed\n${toLogString({ url, error })}`);
      throw error;
    }
  };
}

export default defineNuxtPlugin(async (nuxtApp: NuxtApp) => {
  if (import.meta.server) return;

  const config = useRuntimeConfig();
  const msalCfg = (config && config.public && config.public.msal) || {};
  const tenant = (msalCfg as any).tenant;
  const clientId = (msalCfg as any).clientId;
  const redirectUri = (msalCfg as any).redirectUri || window.location.origin;
  const authority = `https://login.microsoftonline.com/${tenant}`;

  const msalApp = await PublicClientApplication.createPublicClientApplication({
    auth: {
      clientId,
      authority,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: false
    },
    // sessionStorage keeps the MSAL token cache out of long-lived localStorage,
    // reducing the XSS/shared-machine exfiltration window for access/refresh tokens.
    cache: { cacheLocation: 'sessionStorage' }
  });

  // Must precede handleRedirectPromise, which performs the first /token call
  logTokenResponses(msalApp.getConfiguration().system.networkClient);

  // Handle AAD redirect response
  const redirectResult: AuthenticationResult | null = await msalApp.handleRedirectPromise();

  if (redirectResult && redirectResult.idToken) {
    setTokenCookie(redirectResult.idToken);

    // Restore pre-login route (saved to sessionStorage before the AAD redirect)
    const { path, hash } = consumeRedirectLocation();
    const target = path || window.location.pathname;
    history.replaceState(null, document.title, window.location.pathname + window.location.search);
    if (target) {
      window.location.replace(target + (hash || ''));
    }
  }

  // Set active account if one exists
  if (!msalApp.getActiveAccount()) {
    const accounts = msalApp.getAllAccounts();
    if (accounts.length > 0) {
      msalApp.setActiveAccount(accounts[0]);
    }
  }

  // Build initial user data from token cookie or active account
  const tokenCookie = useCookie('token');
  const existingToken = tokenCookie.value;
  const initialPayload = existingToken ? parseJwt(existingToken) : null;

  const data = {
    user: initialPayload ? { idToken: initialPayload } : null
  };

  // Shared in-flight renewal so concurrent callers (check-token, clientInit,
  // internalService) coalesce onto a single acquireTokenSilent instead of each
  // firing their own — and racing loginRedirect fallbacks — on one navigation.
  let silentInFlight: Promise<boolean> | null = null;

  // An interactive redirect is a page-leaving navigation that msal-browser builds
  // asynchronously (PKCE, authorize URL). Several callers can reach that decision on
  // the same tick; letting each start its own leaves MSAL in `interaction_in_progress`
  // and makes the surviving one fail. One flag, one redirect.
  let loginRedirectStarted = false;

  const msal: MsalShim = {
    data,
    hasValidToken() {
      const currentToken = useCookie('token').value;
      if (!currentToken) return false;
      const payload = parseJwt(currentToken);
      return !!(payload && payload.exp && payload.exp > Date.now() / 1000);
    },
    isLoginInProgress() {
      return loginRedirectStarted;
    },
    isAuthenticated() {
      // Any token cookie counts, expired or not. The cookie outlives the JWT on purpose:
      // an expired one means "signed in, needs renewal" (callers then see
      // hasValidToken() === false and re-login), not "anonymous" — which is what it
      // would look like in a new tab or after a browser restart, where the
      // sessionStorage MSAL cache below is empty.
      if (useCookie('token').value) return true;
      // Fallback: check MSAL account cache
      const account = msalApp.getActiveAccount() || msalApp.getAllAccounts()[0];
      return !!account;
    },
    silentLogin(_opts: any) {
      // Reuse an in-flight renewal so overlapping callers on the same navigation
      // don't each issue their own request (and their own redirect on failure).
      if (silentInFlight) return silentInFlight;
      const scopes = _opts && Array.isArray(_opts.scopes) ? _opts.scopes : ['openid', 'profile', 'email'];
      const account = msalApp.getActiveAccount() || msalApp.getAllAccounts()[0];
      if (!account) {
        return Promise.resolve(false);
      }
      const req: SilentRequest & { loginHint?: string } = { scopes, account };
      // Prefer an explicit hint; otherwise fall back to the cached account's
      // username. Without a token cookie (and thus data.user) the caller can't
      // derive a hint, but the MSAL account still carries one — so silent
      // renewal keeps working instead of forcing an interactive redirect.
      const loginHint = (_opts && _opts.loginHint) || account.username;
      if (loginHint) req.loginHint = loginHint;
      silentInFlight = msalApp
        .acquireTokenSilent(req)
        .then((response: AuthenticationResult) => {
          if (!response.idToken) return false;
          setTokenCookie(response.idToken);
          const payload = parseJwt(response.idToken);
          if (payload) {
            msal.data.user = { idToken: payload };
          }
          return true;
        })
        .catch(() => {
          return false;
        })
        .finally(() => {
          silentInFlight = null;
        });
      return silentInFlight;
    },
    loginRedirect(_opts: any) {
      if (loginRedirectStarted) return;
      loginRedirectStarted = true;
      // Preserve current route to come back after login. Persist to sessionStorage
      // (the same place handleRedirectPromise restores from). Skip if a caller such
      // as check-token already saved a more specific target.
      if (!hasRedirectLocation()) {
        saveRedirectLocation();
      }
      const scopes = _opts && Array.isArray(_opts.scopes) ? _opts.scopes : ['openid', 'profile', 'email'];
      Promise.resolve(msalApp.loginRedirect({ scopes })).catch(() => {
        // The navigation never happened, so the next caller may legitimately retry.
        loginRedirectStarted = false;
      });
    },
    logout() {
      useCookie('token').value = null;
      msalApp.logoutRedirect();
    }
  };

  // Keep msal.data in sync with cookie on visibility change
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const currentToken = useCookie('token').value;
      const payload = currentToken ? parseJwt(currentToken) : null;
      msal.data.user = payload ? { idToken: payload } : null;
    }
  });

  // Expose on window for legacy calls (window.msal.loginRedirect)
  window.msal = msal;

  // Inject into Nuxt app (guard against double injection on hydration)
  if (!nuxtApp.$msal) {
    nuxtApp.provide('msal', msal);
  } else {
    window.msal = nuxtApp.$msal;
  }

  // Attach to Pinia stores as store.$msal for legacy code
  if (nuxtApp.$pinia && typeof (nuxtApp.$pinia as any).use === 'function') {
    (nuxtApp.$pinia as any).use(({ store }: { store: any }) => {
      store.$msal = msal;
      // Only the main store drives auth-gating. Gate on `isGlobal` rather than
      // `lang === 'global'`: `lang` carries 'global' as its *initial* value too, so
      // it cannot tell "this is the global channel" apart from "serverInit has not
      // resolved the host yet". `isGlobal` is false until serverInit has actually
      // derived the channel from the host, so it never fires on an unresolved store.
      // The test is `isAuthenticated()` — no session at all — and nothing else: a
      // signed-in user who simply lacks internal access must never be sent to AAD,
      // because AAD returns instantly on a live session and the app would bounce
      // forever.
      if (store.$id === 'main' && store.isGlobal && !msal.isAuthenticated()) {
        msal.loginRedirect({ scopes: ['user.read'] });
      }
    });
  }
});
